import { oc } from "@orpc/contract";
import { z } from "zod";
import { VatTreatment } from "./cart";
import { AddressCountry } from "./customer-address";
import { listInput, listPage } from "./list";
import { Cents } from "./money";

/**
 * Invoices (CONTEXT.md `invoice`): one per paid order, numbered `CBI-100000…` without gaps
 * (#94), with its pdf in R2 (#95). Issued by the `OrderPaidWorkflow` after a payment (#96); the
 * orders admin can issue it by hand too (idempotent: an order never gets a second invoice).
 *
 * An invoice is a snapshot of the seller (Bedrijfsgegevens), the customer and the order's lines
 * and VAT at the moment it was issued.
 *
 * On account (#97, #98): the invoice is issued when the order is placed, with `dueOn` (the
 * customer's payment term) and Pay.nl's transfer reference, and `paidOn` stays `null` until Pay.nl
 * reports the transfer. Open on-account invoices are listed (`open`), overdue ones marked; an
 * employee mails a reminder by hand (`remind`). Nothing is sent automatically.
 */

export const InvoiceLine = z.object({
	key: z.string(),
	description: z.string(),
	sku: z.string().nullable(),
	quantity: z.number().int(),
	/** On the invoice's price basis: incl. VAT for a consumer, excl. VAT for a business. */
	unitPrice: Cents,
	discount: Cents,
	vatRate: z.number().int(),
	excl: Cents,
	vat: Cents,
	incl: Cents,
});
export type InvoiceLine = z.infer<typeof InvoiceLine>;

export const InvoiceVatRate = z.object({ vatRate: z.number().int(), excl: Cents, vat: Cents, incl: Cents });

export const Invoice = z.object({
	id: z.string(),
	/** `CBI-100000`. */
	number: z.string(),
	orderId: z.string(),
	orderNumber: z.string(),
	/** `YYYY-MM-DD`. */
	issuedOn: z.string(),
	/** When it was paid; `null` for an on-account invoice not paid yet. */
	paidOn: z.string().nullable(),
	/** On account: the last day to pay; `null` for an order paid at checkout. */
	dueOn: z.string().nullable(),
	/** On account: Pay.nl's payment reference and its page with the account details, when it gave them. */
	transferReference: z.string().nullable(),
	transferUrl: z.string().nullable(),
	vatTreatment: VatTreatment,
	/** "Btw verlegd": the invoice states it. */
	reverseCharge: z.boolean(),
	customer: z.object({
		name: z.string(),
		companyName: z.string().nullable(),
		vatId: z.string().nullable(),
		street: z.string(),
		postcode: z.string(),
		city: z.string(),
		country: AddressCountry,
		email: z.string(),
	}),
	lines: z.array(InvoiceLine),
	vatRates: z.array(InvoiceVatRate),
	totalExcl: Cents,
	totalVat: Cents,
	totalIncl: Cents,
	/** The admin's download of the pdf (`invoice.pdf`), root-relative. */
	pdfUrl: z.string(),
	/** When the pdf was stored; null while it is not rendered yet (the download renders it then). */
	pdfStoredAt: z.iso.datetime().nullable(),
});
export type Invoice = z.infer<typeof Invoice>;

/**
 * What became of the customer's confirmation after "Factuur maken": `sent` now, `failed` (the pdf
 * or the mail failed: the invoice stands, the employee sends it again with the same request), or
 * `alreadySent` (it went out before; nothing was sent now).
 */
export const InvoiceConfirmation = z.enum(["sent", "failed", "alreadySent"]);
export type InvoiceConfirmation = z.infer<typeof InvoiceConfirmation>;

/** The answer to "Factuur maken": the invoice, and what became of the confirmation. */
export const IssuedInvoice = z.object({ invoice: Invoice, confirmation: InvoiceConfirmation });
export type IssuedInvoice = z.infer<typeof IssuedInvoice>;

const OrderPath = z.object({ orderId: z.string().min(1) });
const InvoicePath = z.object({ invoiceId: z.string().min(1) });

/** `notDue`: open, the due date not passed; `overdue`: open past its due date. */
export const OpenInvoiceState = z.enum(["notDue", "overdue"]);
export type OpenInvoiceState = z.infer<typeof OpenInvoiceState>;

/** An on-account invoice that is not paid yet, as the list of open invoices shows it. */
export const OpenInvoice = z.object({
	id: z.string(),
	/** `CBI-100000`. */
	number: z.string(),
	orderId: z.string(),
	orderNumber: z.string(),
	customerId: z.string(),
	/** The company on the invoice, else the name. */
	customerName: z.string(),
	email: z.string(),
	issuedOn: z.string(),
	dueOn: z.string(),
	totalIncl: Cents,
	state: OpenInvoiceState,
	/** Days past `dueOn`, counted in Dutch days; 0 while not overdue. */
	daysOverdue: z.number().int(),
	/** When the last reminder was mailed (or logged outside production); `null` for none. */
	lastReminderAt: z.iso.datetime().nullable(),
	/** A reminder went out today (a Dutch day): another one asks for `again`. */
	remindedToday: z.boolean(),
	reminders: z.number().int(),
});
export type OpenInvoice = z.infer<typeof OpenInvoice>;

/** `again`: send it although a reminder went out today already (the employee confirmed). */
export const RemindInvoiceBody = z.object({ again: z.boolean().optional() });
export type RemindInvoiceBody = z.infer<typeof RemindInvoiceBody>;

export const OpenInvoiceListInput = listInput({
	sort: ["dueOn", "number", "customer", "total"],
	defaultSort: "dueOn",
	defaultOrder: "asc",
	filters: { state: OpenInvoiceState.optional().catch(undefined) },
});
export type OpenInvoiceListInput = z.input<typeof OpenInvoiceListInput>;
export type OpenInvoiceListQuery = z.output<typeof OpenInvoiceListInput>;

export const OpenInvoiceList = listPage(OpenInvoice).extend({
	/** Over every open invoice, whatever the filter: how many and how much is overdue. */
	overdueCount: z.number().int(),
	overdueTotal: Cents,
});
export type OpenInvoiceList = z.infer<typeof OpenInvoiceList>;

export const InvoiceContract = {
	/** The order's invoice. Errors: `InvoiceNotFound` (404): the order has none (yet). */
	forOrder: oc
		.route({ method: "GET", path: "/admin/orders/{orderId}/invoice" })
		.input(OrderPath)
		.output(Invoice),
	/**
	 * Issues the order's invoice now, or answers the one it has. A new invoice (or one whose
	 * confirmation has not gone out, or the order still waits for in the work queue, `createInvoice`)
	 * is followed by its pdf and the customer's order confirmation with it, and the `createInvoice`
	 * attention is resolved. Answers the invoice and `confirmation`: a pdf or mail that fails does not
	 * fail the request (the invoice exists) but answers `failed`. Errors:
	 * `OrderNotFound` (404), `OrderNotInvoiceable` (409, `reason`): the order is not paid, or
	 * cancelled (`notPaid`), or reverse charge without the customer's VAT id (`missingVatId`).
	 */
	issueForOrder: oc
		.route({ method: "POST", path: "/admin/orders/{orderId}/invoice" })
		.input(OrderPath)
		.output(IssuedInvoice),
	/** The invoice as a pdf download (rendered first when it was not yet). Errors: `InvoiceNotFound`. */
	pdf: oc
		.route({ method: "GET", path: "/admin/invoices/{invoiceId}/pdf" })
		.input(InvoicePath)
		.output(z.file()),
	/**
	 * The on-account invoices not paid yet, the due date nearest first: search on invoice number,
	 * order number, name, company and email; filter on overdue or not.
	 */
	open: oc
		.route({ method: "GET", path: "/admin/invoices/open" })
		.input(OpenInvoiceListInput)
		.output(OpenInvoiceList),
	/**
	 * Mails the customer a payment reminder for an open on-account invoice, with the amount, the due
	 * date and how to pay. Logged in the mail log and as a `reminderSent` order event, signed with
	 * the employee. Errors: `InvoiceNotFound` (404), `InvoiceNotOpen` (409: paid, cancelled, or not
	 * on account), `ReminderSentToday` (409: one went out today already; send `again` to send
	 * another), `ReminderNotSent` (502: the mail failed; it is in the mail log).
	 */
	remind: oc
		.route({ method: "POST", path: "/admin/invoices/{invoiceId}/reminder" })
		.input(InvoicePath.extend(RemindInvoiceBody.shape))
		.output(OpenInvoice),
	/**
	 * The customer's link from the order confirmation: the pdf, for whoever holds the signed token.
	 * Errors: `InvoiceNotFound` (404), also for a token that is not ours.
	 */
	shopPdf: oc
		.route({ method: "GET", path: "/shop/invoices/{token}" })
		.input(z.object({ token: z.string().min(1).max(512) }))
		.output(z.file()),
	/** A credit invoice (`CBC-…`, #104) as a pdf download. Errors: `CreditInvoiceNotFound` (404). */
	creditPdf: oc
		.route({ method: "GET", path: "/admin/credit-invoices/{creditInvoiceId}/pdf" })
		.input(z.object({ creditInvoiceId: z.string().min(1) }))
		.output(z.file()),
	/**
	 * The customer's link from the credit invoice mail. Errors: `CreditInvoiceNotFound` (404), also
	 * for a token that is not ours.
	 */
	shopCreditPdf: oc
		.route({ method: "GET", path: "/shop/credit-invoices/{token}" })
		.input(z.object({ token: z.string().min(1).max(512) }))
		.output(z.file()),
};
